import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { HighlightInfo } from "@/utils/portfolioUtils";

/**
 * Highlighting by a saved view (an "advanced filter").
 *
 * A saved view is a matching rule, so there is nothing about it that restricts
 * it to filtering — "mark anything matching Fed Watch" is as valid as "show
 * only Fed Watch". Both feed families already carry a client-side evaluator for
 * a CategoryQuery (headlineFilterMatcher / firehoseFilterMatcher), so this is
 * just those matchers applied per item and mapped onto the existing
 * HighlightInfo shape every feed row already renders.
 */

/**
 * A saved view has no colour of its own (see CategoryQuery), so one is derived
 * from its id: stable across sessions and distinguishable when several views
 * are highlighted at once, without a schema change.
 *
 * Chosen to read on the near-black feed with black text, and to stay clear of
 * the red a new watchlist defaults to.
 */
const VIEW_COLORS = [
  "#00AEEF", // squawk blue
  "#F5A623", // amber
  "#7ED321", // green
  "#BD10E0", // magenta
  "#50E3C2", // teal
  "#FFD400", // yellow
];

export function savedViewColor(viewId: number | undefined): string {
  if (viewId === undefined) return VIEW_COLORS[0];
  return VIEW_COLORS[Math.abs(viewId) % VIEW_COLORS.length];
}

/**
 * First selected view matching `item`, as a HighlightInfo.
 *
 * Selection order is the user's own priority — the panel preserves the order
 * boxes were ticked — mirroring how portfolio highlights resolve by priority.
 * Returns undefined when nothing matches, so callers can fall back to (or
 * prefer) a portfolio highlight.
 */
export function resolveSavedViewHighlight<T>(
  item: T,
  views: CategoryQuery[],
  matcher: (item: T, view: CategoryQuery) => boolean
): HighlightInfo | undefined {
  for (const view of views) {
    // An empty view matches everything (see headlineFilterMatcher); as a
    // highlight that would paint the whole feed, which is never intended.
    if (isEmptyView(view)) continue;
    if (matcher(item, view)) {
      return {
        portfolioId: view.id ?? -1,
        portfolioName: view.queryName,
        color: savedViewColor(view.id),
        matchedTickers: [],
      };
    }
  }
  return undefined;
}

function isEmptyView(view: CategoryQuery): boolean {
  return (
    (view.categories?.length ?? 0) === 0 &&
    !view.searchKeyword?.trim() &&
    (view.portfolios?.length ?? 0) === 0 &&
    (view.tagsWatchlists?.length ?? 0) === 0
  );
}

/** Resolves the selected view ids from a highlight selection map. */
export function selectedSavedViews(
  selectedIds: string[] | undefined,
  available: CategoryQuery[] | undefined
): CategoryQuery[] {
  if (!selectedIds?.length || !available?.length) return [];
  return selectedIds
    .map((id) => available.find((view) => String(view.id) === id))
    .filter((view): view is CategoryQuery => !!view);
}
